// HTML field variable processing utilities

import { getSubmissionTimestamp } from "./submissionUtils";

/**
 * Processes HTML content by replacing variables with actual values
 * @param htmlContent - Raw HTML content with variables
 * @param variables - Object containing variable values
 * @returns Processed HTML content
 */
export const processHtmlVariables = (htmlContent: string, variables: Record<string, any> = {}): string => {
  if (!htmlContent) return '';
  
  let processedContent = htmlContent;
  
  // Replace variables in the format {{variableName}}
  const variableRegex = /\{\{([^}]+)\}\}/g;
  
  processedContent = processedContent.replace(variableRegex, (match, variableName) => {
    const trimmedName = variableName.trim();
    
    if (variables.hasOwnProperty(trimmedName)) {
      const value = variables[trimmedName];
      
      // Handle different data types
      if (value === null || value === undefined) {
        return '';
      } else if (Array.isArray(value)) {
        return value.join(', ');
      } else if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      } else {
        return String(value);
      }
    }
    
    // Return original if variable not found
    return match;
  });
  
  return processedContent;
};

/**
 * Gets available system variables for current context
 * @param formData - Form data object
 * @param submissionData - Submission data object (optional)
 * @returns Object with system variables
 */
export const getSystemVariables = (formData?: any, submissionData?: any): Record<string, any> => {
  const variables: Record<string, any> = {};
  
  // Form-related variables
  if (formData) {
    variables.formTitle = formData.title || 'Form';
    variables.formDescription = formData.description || '';
  }
  
  // Submission-related variables (available when viewing/editing submissions)
  if (submissionData) {
    variables.submissionId = submissionData.id || '';
    variables.uniqueId = submissionData.uniqueId || '';
    variables.intUniqueId = getSubmissionTimestamp(submissionData.uniqueId);
    variables.submissionTimestamp = submissionData.createdAt ? new Date(submissionData.createdAt).toLocaleString() : '';
    variables.submissionDate = submissionData.createdAt ? new Date(submissionData.createdAt).toLocaleDateString() : '';
    variables.submissionStatus = submissionData.status || '';
    
    // Special variable for all fields (formatted)
    if (submissionData.data && formData?.fields) {
      const allFieldsFormatted = formData.fields
        .filter((field: any) => field.type !== 'html' && field.type !== 'heading' && field.type !== 'paragraph' && field.type !== 'divider')
        .map((field: any) => {
          const value = submissionData.data[field.name] || submissionData.data[field.id] || '';
          const displayValue = Array.isArray(value) ? value.join(', ') : value;
          return `${field.label}: ${displayValue}`;
        })
        .join('\n');
      variables.allFields = allFieldsFormatted;
    }
  } else {
    // Preview mode variables
    variables.submissionId = '[Preview Mode]';
    variables.uniqueId = '[Preview Mode]';
    variables.intUniqueId = '[Preview Mode]';
    variables.submissionTimestamp = new Date().toLocaleString();
    variables.submissionDate = new Date().toLocaleDateString();
    variables.submissionStatus = 'Preview';
    variables.allFields = '[All form data will appear here]';
  }
  
  return variables;
};

/**
 * Gets available form field variables
 * @param formFields - Array of form fields
 * @param submissionData - Submission data object (optional)
 * @returns Object with field variables
 */
export const getFormFieldVariables = (formFields: any[] = [], submissionData?: any): Record<string, any> => {
  const variables: Record<string, any> = {};
  
  formFields
    .filter(field => field.type !== 'html' && field.type !== 'heading' && field.type !== 'paragraph' && field.type !== 'divider')
    .forEach(field => {
      if (submissionData?.data) {
        // Use actual submission data
        const value = submissionData.data[field.name] || submissionData.data[field.id] || '';
        variables[field.name] = Array.isArray(value) ? value.join(', ') : value;
      } else {
        // Preview mode
        variables[field.name] = `[${field.label || field.name}]`;
      }
    });
  
  return variables;
};

/**
 * Sanitizes HTML content for safe rendering
 * Note: This is a basic sanitizer. For production use, consider using a library like DOMPurify
 * @param htmlContent - HTML content to sanitize
 * @returns Sanitized HTML content
 */
export const sanitizeHtmlContent = (htmlContent: string): string => {
  if (!htmlContent) return '';
  
  // Remove script tags and their content
  let sanitized = htmlContent.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers (onclick, onload, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  
  // Remove javascript: protocols
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Remove potentially dangerous attributes
  sanitized = sanitized.replace(/\s*(src|href)\s*=\s*["']javascript:[^"']*["']/gi, '');
  
  return sanitized;
};

/**
 * Combines all variables for HTML processing
 * @param formData - Form data
 * @param submissionData - Submission data (optional)
 * @returns Combined variables object
 */
export const getAllVariables = (formData?: any, submissionData?: any): Record<string, any> => {
  const systemVars = getSystemVariables(formData, submissionData);
  const fieldVars = getFormFieldVariables(formData?.fields || [], submissionData);
  
  return {
    ...systemVars,
    ...fieldVars
  };
};
